import * as Sentry from '@sentry/react'
import CONFIG from 'config'
import { getAuthenticationToken } from 'utils'

// HTTP client for classfy-api (Odin). Parallel to ../index.js but:
// - BASE_URL points to CONFIG.classfyApiURL
// - Response is plain JSON (no { data } envelope)
// - Errors use NestJS shape: { statusCode, message, error } and can carry
//   `message` as string or string[] (class-validator). We normalise it.
export const BASE_URL = CONFIG.classfyApiURL
export const COMMON_ERROR_MESSAGE = 'Desafortunadamente algo ha ido mal...'
const JWT_ERROR = 'jwtError'

function makeError(error = {}) {
  const customError = new Error(error?.userMessage ?? COMMON_ERROR_MESSAGE)
  customError.code = error?.code
  customError.originalMessage = error?.message
  customError.userMessage = error?.userMessage
  customError.statusCode = error?.statusCode
  customError.isLogin = error?.isLogin
  return customError
}

const processResponse = async response => {
  if (response.ok) {
    // Some classfy-api endpoints respond with 204 No Content or with 200 and
    // an empty body. `response.json()` on an empty body throws SyntaxError,
    // which would be surfaced as a generic error even though the request
    // succeeded. Read as text first and parse only if there is content.
    if (response.status === 204) return undefined
    const text = await response.text()
    if (!text) return undefined
    try {
      return JSON.parse(text)
    } catch (_) {
      return undefined
    }
  }
  let body = {}
  try {
    body = await response.json()
  } catch (_) {}
  const message = Array.isArray(body.message)
    ? body.message.join(', ')
    : body.message || COMMON_ERROR_MESSAGE
  const code = response.status === 401 || response.status === 403 ? JWT_ERROR : body.code
  throw makeError({
    code,
    message,
    userMessage: message,
    statusCode: response.status
  })
}

function makeHeaders(useAuth = false, useAcceptAPP_JSON = false) {
  return useAuth
    ? {
        ...(useAcceptAPP_JSON
          ? { Accept: 'application/json' }
          : { 'Content-Type': 'application/json' }),
        Authorization: `Bearer ${getAuthenticationToken()}`
      }
    : {
        ...(useAcceptAPP_JSON
          ? { Accept: 'application/json' }
          : { 'Content-Type': 'application/json' })
      }
}

export function GET({ url = '', useAuth = true } = {}) {
  return fetch(BASE_URL + url, {
    method: 'GET',
    headers: makeHeaders(useAuth)
  })
    .then(processResponse)
    .catch(e => {
      Sentry.captureException(e)
      Sentry.logger.error('Odin API call failed', { error: e, url })
      throw makeError(e)
    })
}

function commonMethod(params) {
  const {
    url = '',
    data = {},
    useAuth = true,
    useAcceptAPP_JSON = false
  } = params
  return method =>
    fetch(BASE_URL + url, {
      method,
      headers: makeHeaders(useAuth, useAcceptAPP_JSON),
      body: useAcceptAPP_JSON ? data : JSON.stringify(data)
    })
      .then(processResponse)
      .catch(e => {
        Sentry.captureException(e)
        Sentry.logger.error('Odin API call failed', { error: e, url })
        throw makeError(e)
      })
}

export const POST = params => commonMethod(params)('POST')
export const PUT = params => commonMethod(params)('PUT')
export const DELETE = params => commonMethod(params)('DELETE')
